
import React, { useState } from 'react';

const PricingSection = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showConsultForm, setShowConsultForm] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter Protocol',
      price: 399,
      period: 'month',
      description: 'Perfect for those new to peptide therapy',
      features: [
        '1-2 peptide protocol',
        'Initial consultation & assessment',
        'Basic laboratory testing',
        'Monthly progress reviews',
        'Injection training & supplies',
        'Email support'
      ],
      popular: false,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'advanced',
      name: 'Advanced Protocol',
      price: 699,
      period: 'month',
      description: 'Comprehensive multi-peptide therapy for optimal results',
      features: [
        '3-4 peptide combination',
        'Comprehensive health assessment',
        'Advanced biomarker testing',
        'Bi-weekly progress monitoring',
        'Custom dosing adjustments',
        'Priority phone & email support',
        'Nutritional guidance'
      ],
      popular: true,
      color: 'from-blue-600 to-blue-700'
    },
    {
      id: 'elite',
      name: 'Elite Protocol',
      price: 999,
      period: 'month',
      description: 'Premium peptide therapy with concierge-level care',
      features: [
        '5+ peptide combinations',
        'Comprehensive metabolic panel',
        'Weekly progress consultations',
        'Real-time dosing optimization',
        'Lifestyle & performance coaching',
        '24/7 medical support',
        'Quarterly in-depth reviews',
        'Access to newest peptides'
      ],
      popular: false,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
    setShowConsultForm(true);
  };

  const handleCloseForm = () => {
    setShowConsultForm(false);
    setSelectedPlan(null);
  };

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
            <strong>Peptide Therapy</strong> Investment Plans
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose the peptide protocol that aligns with your health goals and budget. All plans include medical supervision and ongoing support.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 ${
                plan.popular ? 'ring-4 ring-blue-500 ring-opacity-50' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}
              
              <div className={`bg-gradient-to-r ${plan.color} p-6 lg:p-8 text-white ${plan.popular ? 'pt-10 lg:pt-12' : ''}`}>
                <h3 className="text-xl lg:text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl lg:text-4xl font-bold">${plan.price}</span>
                  <span className="text-base lg:text-lg opacity-80 ml-2">/{plan.period}</span>
                </div>
                <p className="text-base lg:text-lg opacity-90">{plan.description}</p>
              </div>

              <div className="p-6 lg:p-8">
                <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-blue-600 text-lg lg:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm lg:text-base text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer bg-gradient-to-r ${plan.color} text-white hover:shadow-lg transform hover:-translate-y-1`}
                >
                  Start {plan.name}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-600 mb-4">
            All plans include comprehensive medical supervision and can be customized to your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <i className="ri-shield-check-line text-blue-600 mr-2"></i>
              Medical Grade Peptides
            </div>
            <div className="flex items-center">
              <i className="ri-user-heart-line text-blue-600 mr-2"></i>
              Expert Medical Supervision
            </div>
            <div className="flex items-center">
              <i className="ri-refresh-line text-blue-600 mr-2"></i>
              Flexible Protocols
            </div>
          </div>
        </div>
      </div>

      {/* Consultation Form Modal */}
      {showConsultForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 lg:p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Schedule Peptide Therapy Consultation
                </h3>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer p-2"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form
                id="peptide-consultation"
                data-readdy-form
                action="https://readdy.ai/api/form/d41shpeqegfu99c5kdpg"
                method="POST"
                className="space-y-4 lg:space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  
                  // Validate required fields
                  const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
                  for (const field of requiredFields) {
                    if (!formData.get(field)) {
                      alert(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
                      return;
                    }
                  }

                  // Submit form
                  fetch('https://readdy.ai/api/form/peptide-therapy-consult', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any)
                  }).then(() => {
                    alert('Consultation request submitted successfully! We will contact you within 24 hours to schedule your appointment.');
                    handleCloseForm();
                  }).catch(() => {
                    alert('There was an error submitting your request. Please try again.');
                  });
                }}
              >
                <input type="hidden" name="selectedPlan" value={selectedPlan || ''} />

                <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Health Goals
                  </label>
                  <textarea
                    name="healthGoals"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="Describe your health and performance goals (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="flex-1 py-3 px-6 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Schedule Consultation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingSection;
